import { ReactNode, useState } from 'react'
import { Card } from 'react-bootstrap'
import { ChangePremiumPlanPopup } from 'web_core/assets/subscription/ChangePremiumPlanPopup'
import { PremiumPlanPaymentPopup } from 'web_core/assets/subscription/PremiumPlanPaymentPopup'
import { useAccount } from 'web_core/context/account'
import { Subscription } from 'web_core/firebase'
import { useAuthState } from 'web_core/firebase/hooks'
import { useStripePortal } from 'web_core/hooks'
import { Interval, Plan } from 'web_core/shared/stripe'
import { Designed } from 'web_core/types'
import { logEvent } from 'web_core/utils'
import styles from '~/styles/_pricing.module.scss'
import { PlanCardActionButton } from './PlanCardActionButton'

export type PlanCardType = 'free' | 'monthly_paid' | 'yearly_paid'

type PlanCardInfo = {
  promotion?: string
  title: string
  desc: ReactNode
  price: 'free' | number
  functions: string[]
  plan: Plan
  interval: Interval | null
}

export const PLANS: Record<PlanCardType, PlanCardInfo> = {
  free: {
    title: '無料プラン',
    desc: '無料で情報発信を始めたい人におすすめ',
    price: 'free',
    functions: ['記事の作成', '本の作成'],
    plan: 'Free',
    interval: null,
  },
  monthly_paid: {
    title: '月払いプラン',
    desc: (
      <>
        特定のイベントに向けて
        <br />
        コンテンツ作成をしたい人におすすめ
      </>
    ),
    price: 1480,
    functions: [
      '無料プランの全機能',
      'TeXエクスポート',
      '共同編集',
      '公開範囲の設定',
      '広告の非表示',
    ],
    plan: 'Premium',
    interval: 'monthly',
  },
  yearly_paid: {
    promotion: '年間 35%お得',
    title: '年払いプラン',
    desc: (
      <>
        日常的に情報共有や学びの記録のために
        <br />
        コンテンツ作成をしたい人におすすめ
      </>
    ),
    price: 980,
    functions: [
      '無料プランの全機能',
      'TeXエクスポート',
      '共同編集',
      '公開範囲の設定',
      '広告の非表示',
    ],
    plan: 'Premium',
    interval: 'yearly',
  },
}

type Props = {
  planType: PlanCardType
  subscription: Subscription | null | undefined
}

export const PlanCard = ({
  planType,
  subscription,
  className,
}: Designed<Props>) => {
  const planInfo = PLANS[planType]
  const { account } = useAccount()

  const [showSubscribeModal, setShowSubscribeModal] = useState(false)
  const [showChangeModal, setShowChangeModal] = useState(false)
  const { showSignInPopup } = useAuthState()
  const { openPortal, opening } = useStripePortal()

  return (
    <>
      <PremiumPlanPaymentPopup
        introduction={false}
        onHide={() => setShowSubscribeModal(false)}
        show={showSubscribeModal}
        defaultInterval={planInfo?.interval ?? undefined}
      />
      {planInfo?.interval && (
        <ChangePremiumPlanPopup
          onHide={() => setShowChangeModal(false)}
          show={showChangeModal}
          target={planInfo.interval}
        />
      )}
      <Card
        className={`${styles['plan-card']} ${
          className != null ? className : ''
        }`}
      >
        <Card.Body
          className={`${styles['card-body']} ${
            planInfo.promotion != null ? styles['promotion'] : ''
          }`}
        >
          <Card.Title className={styles['card-title']} as="h3">
            {planInfo.title}
          </Card.Title>
          <p className={styles['card-description']}>{planInfo.desc}</p>
          <div>
            {planInfo.price === 'free' ? (
              <span className={styles['card-price']}>Free</span>
            ) : (
              <>
                <span className={styles['sub-text']}>¥</span>
                <span className={styles['card-price']}>
                  {planInfo.price
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </span>
                <span className={styles['sub-text']}>/月</span>
              </>
            )}
          </div>
          <hr className={styles.hr} />
          <div className={styles['plan-features']}>
            {planInfo.functions.map((func, index) => (
              <div className="py-1" key={index}>
                {func}
              </div>
            ))}
          </div>
          <PlanCardActionButton
            planType={planType}
            subscription={subscription}
            openPortal={openPortal}
            opening={opening}
            showSubscribePlanModal={() => {
              // プランカードからサブスクしようとした際のイベント収集
              logEvent('tap_pricing_card_subscribe', {
                currentPlan: account?.plan,
                planType,
              })
              setShowSubscribeModal(true)
            }}
            showChangePlanModal={() => {
              // プランカードからプラン変更しようとした際のイベント収集
              logEvent('tap_pricing_card_change', {
                currentPlan: account?.plan,
                planType,
              })
              setShowChangeModal(true)
            }}
            showLoginPopup={() => showSignInPopup()}
          />
        </Card.Body>
        {planInfo?.promotion !== undefined && (
          <div className={`bg-primary ${styles['promotion-bg']}`}>
            <span>{planInfo.promotion}</span>
          </div>
        )}
      </Card>
    </>
  )
}
